/** Audio helpers shared by the composer, comments and chat. */
import { formatBytes, formatTime } from './media';

// Re-exported so older imports keep working (single implementation in media.js).
export { formatBytes, formatTime };

/** The upload limit the platform allows for one audio file. */
export const MAX_AUDIO_MB = 10;
export const MAX_AUDIO_BYTES = MAX_AUDIO_MB * 1024 * 1024;

/** Audio extensions we accept (mp3 + the formats browsers record). */
export const AUDIO_ACCEPT = 'audio/mpeg,audio/mp3,audio/mp4,audio/m4a,audio/aac,audio/ogg,audio/opus,audio/webm,audio/wav,.mp3,.m4a,.wav,.ogg,.webm';

export const AUDIO_EXT_OK = /\.(mp3|m4a|mp4|aac|ogg|oga|opus|webm|wav|flac)$/i;

/** Client-side guard before hitting the API (the API checks again). */
export function audioError(file) {
  if (!file) return 'Pick an audio file first';
  if (file.size > MAX_AUDIO_BYTES) {
    return `The audio is ${formatBytes(file.size)} — the maximum is ${MAX_AUDIO_MB} MB`;
  }
  const name = (file.name || '').toLowerCase();
  if (file.type && !/^audio\//.test(file.type) && !AUDIO_EXT_OK.test(name)) {
    return 'Unsupported audio type (use MP3, M4A, WAV, OGG or WEBM)';
  }
  return '';
}

/**
 * Voice recording with the microphone (MediaRecorder).
 * Returns `null` when the browser cannot record — the UI then hides the button.
 *
 *   const rec = startVoiceRecording();
 *   const { blob, seconds, url } = await rec.stop();
 */
export function startVoiceRecording() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia
      || typeof MediaRecorder === 'undefined') {
    return null;
  }
  const chunks = [];
  let recorder = null;
  let stream = null;
  let startedAt = 0;
  let stopPromise = null;

  const pickMime = () => {
    const options = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    return options.find((t) => {
      try { return MediaRecorder.isTypeSupported(t); } catch (e) { return false; }
    }) || '';
  };

  return {
    async start() {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickMime();
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      stopPromise = new Promise((resolve) => { recorder.onstop = resolve; });
      startedAt = Date.now();
      recorder.start();
      return this;
    },
    get seconds() { return Math.round((Date.now() - startedAt) / 1000); },
    /** Stop and hand back the recorded blob (or null when nothing was recorded). */
    async stop() {
      if (!recorder) return null;
      try { recorder.stop(); } catch (e) { /* already stopped */ }
      await stopPromise;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      const seconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
      if (!chunks.length) return null;
      const type = recorder.mimeType || 'audio/webm';
      const blob = new Blob(chunks, { type });
      const ext = /mp4/.test(type) ? 'm4a' : (/ogg/.test(type) ? 'ogg' : 'webm');
      const file = new File([blob], `voice-${Date.now()}.${ext}`, { type });
      return { blob, file, seconds, url: URL.createObjectURL(blob) };
    },
    /** Abort without keeping anything. */
    cancel() {
      try { recorder && recorder.stop(); } catch (e) { /* ignore */ }
      if (stream) stream.getTracks().forEach((t) => t.stop());
      chunks.length = 0;
    },
  };
}

/**
 * Read an image file from disk and return a data URL (used by the cropper).
 */
export function readImageFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('cannot read the image'));
    reader.readAsDataURL(file);
  });
}

/** Duration of an audio file (data URL / object URL / http url) in seconds. */
export function measureAudio(src) {
  return new Promise((resolve) => {
    const el = document.createElement('audio');
    el.preload = 'metadata';
    el.onloadedmetadata = () => resolve(Math.round(el.duration || 0));
    el.onerror = () => resolve(0);
    el.src = src;
  });
}
